import { useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Plus, Trash2, Loader2, CheckCircle, Play, AlertCircle, Blocks, Code2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { useMode } from "../context/ModeContext";
import { MODES, MODE_META, normalizeMode } from "../lib/modes";
import { BlocksInputsEditor } from "../components/admin/BlocksInputsEditor";
import { NavGridEditor, navProblems } from "../components/nav/NavGridEditor";
import { NavRulesEditor } from "../components/nav/NavRulesEditor";
import { BlockRulesEditor } from "../components/blocks/BlockRulesEditor";
import { BLOCKS_CATEGORIES } from "../components/blocks/blocksSetup";
import { NavPlayback } from "../components/nav/NavPlayback";
import { BlocksWorkspace } from "../components/blocks/BlocksWorkspace";
import { countNavBlocks } from "../components/blocks/navBlocks";
import { emptyNav } from "../components/nav/navShared";
import { clientHeaders } from "../lib/clientId";
import { clearDraft } from "../lib/blocksDraft";
import CodeMirror from "@uiw/react-codemirror";
import { python } from "@codemirror/lang-python";
import { oneDark } from "@codemirror/theme-one-dark";

const API = "/api";

export default function CreateQuestion({ user, setUser }) {
  const navigate = useNavigate();
  const { mode: siteMode } = useMode();
  const [track, setTrackState] = useState(normalizeMode(siteMode));
  const setTrack = (t) => { setTrackState(t); setNavVerified(false); };
  const [blocksInputs, setBlocksInputs] = useState([]);
  const [blocksRules, setBlocksRules] = useState({ excluded_categories: [], excluded_blocks: [] });
  const [nav, setNav] = useState(() => emptyNav());
  const [navSolution, setNavSolution] = useState({ code: "", workspace: null });
  const [navVerified, setNavVerified] = useState(false);
  const navPlaybackRef = useRef(null);
  const isNav = track === "junior";
  const navUsed = countNavBlocks(navSolution.workspace);
  const navProblem = nav.max_blocks && navUsed > nav.max_blocks ? `Too many blocks: ${navUsed} used, ${nav.max_blocks} allowed (loops, ifs and function definitions count as 2)` : null;
  const onNavChange = useCallback((code, workspace) => { setNavSolution({ code, workspace }); setNavVerified(false); }, []);
  const updateNav = (next) => { setNav(next); setNavVerified(false); };
  const runNav = useCallback(async () => {
    const r = await fetch(`${API}/code/nav-run`, { method: "POST", headers: clientHeaders(), credentials: "include", body: JSON.stringify({ nav, code: navSolution.code, workspace: navSolution.workspace }) });
    const d = await r.json();
    if (!r.ok) throw new Error(d.detail?.message || d.detail || "Run failed");
    return d;
  }, [nav, navSolution]);
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [contributorBonus, setContributorBonus] = useState(0);
  const [validationResult, setValidationResult] = useState(null);
  const [quickTestInput, setQuickTestInput] = useState("");
  const [quickTestOutput, setQuickTestOutput] = useState("");
  const [quickTesting, setQuickTesting] = useState(false);
  
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    input_format: "",
    output_format: "",
    example_input: "",
    example_output: "",
    sample_solution: "# Write your Python solution here\n"
  });

  const [testCases, setTestCases] = useState([
    { input: "", expected_output: "" },
    { input: "", expected_output: "" }
  ]);

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
    setValidationResult(null);
  };

  const handleTestCaseChange = (index, field, value) => {
    setTestCases(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
    setValidationResult(null);
  };

  const runQuickTest = async () => {
    if (!formData.sample_solution.trim()) {
      toast.error("Please write some code first");
      return;
    }

    setQuickTesting(true);
    setQuickTestOutput("");

    try {
      const response = await fetch(`${API}/code/test`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          code: formData.sample_solution,
          custom_input: quickTestInput
        })
      });

      const data = await response.json();
      
      if (data.success) {
        setQuickTestOutput(data.output?.trim() || "(No output)");
      } else {
        setQuickTestOutput(`Error: ${data.error || "Unknown error"}`);
      }
    } catch (error) {
      setQuickTestOutput(`Error: ${error.message}`);
    } finally {
      setQuickTesting(false);
    }
  };

  const addTestCase = () => {
    if (testCases.length >= 5) {
      toast.error("Maximum 5 test cases allowed");
      return;
    }
    setTestCases(prev => [...prev, { input: "", expected_output: "" }]);
  };

  const removeTestCase = (index) => {
    if (testCases.length <= 2) {
      toast.error("At least 2 test cases are required");
      return;
    }
    setTestCases(prev => prev.filter((_, i) => i !== index));
  };

  const validateSolution = async () => {
    // Basic validation
    if (!formData.sample_solution.trim()) {
      toast.error("Please provide a sample solution");
      return;
    }

    const validTestCases = testCases.filter(tc => tc.input.trim() || tc.expected_output.trim());
    if (validTestCases.length < 2) {
      toast.error("Please provide at least 2 test cases");
      return;
    }

    setValidating(true);
    setValidationResult(null);

    try {
      // Test each test case
      const results = [];
      for (const tc of validTestCases) {
        const response = await fetch(`${API}/code/test`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            code: formData.sample_solution,
            custom_input: tc.input
          })
        });

        const data = await response.json();
        const actualOutput = data.output?.trim() || "";
        const expectedOutput = tc.expected_output.trim();
        const passed = data.success && actualOutput === expectedOutput;
        
        results.push({
          input: tc.input,
          expected: expectedOutput,
          actual: actualOutput,
          passed,
          error: data.error
        });
      }

      const allPassed = results.every(r => r.passed);
      setValidationResult({ results, allPassed });

      if (allPassed) {
        toast.success("All test cases passed!");
      } else {
        toast.error("Some test cases failed. Please fix your solution.");
      }
    } catch (error) {
      toast.error("Failed to validate solution");
      console.error("Validation error:", error);
    } finally {
      setValidating(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!user) {
      toast.error("Please login to submit a question");
      return;
    }

    // Validation
    if (!formData.title || (!isNav && (!formData.description || !formData.sample_solution))) {
      toast.error("Please fill in all required fields");
      return;
    }

    const validTestCases = isNav ? [] : testCases.filter(tc => tc.input.trim() || tc.expected_output.trim());
    if (isNav) {
      const problems = navProblems(nav);
      if (problems.length) { toast.error(problems[0]); return; }
      if (!navVerified) { toast.error("Press Play and reach the treasure with your blocks before submitting"); return; }
    } else if (validTestCases.length < 2) {
      toast.error("Please provide at least 2 test cases");
      return;
    }

    // Must validate before submitting
    if (!isNav && !validationResult?.allPassed) {
      toast.error("Please validate your solution before submitting");
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API}/questions/submit`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          ...formData,
          ...(isNav ? { description: "", input_format: "", output_format: "", example_input: "", example_output: "", sample_solution: navSolution.code } : {}),
          test_cases: validTestCases,
          mode: track,
          blocks: isNav
            ? { kind: "nav", inputs: [], nav, solution_workspace: navSolution.workspace, solution_python: navSolution.code, solution_source: "manual" }
            : track === "blocks" ? { inputs: blocksInputs, ...blocksRules } : null,
        })
      });

      if (response.ok) {
        const data = await response.json();
        setContributorBonus(data.contributor_bonus || 0);
        setSubmitted(true);
        
        // Update user score immediately if bonus was awarded
        if (data.contributor_bonus > 0 && setUser && user) {
          setUser(prev => ({
            ...prev,
            total_score: (prev.total_score || 0) + data.contributor_bonus
          }));
          toast.success(`Question submitted! +${data.contributor_bonus} Contributor Bonus points!`);
        } else {
          toast.success("Question submitted for review!");
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to submit question");
      }
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen">
        <div className="container max-w-xl mx-auto px-4 py-12 text-center">
          <h1 className="text-2xl font-bold mb-4">Login Required</h1>
          <p className="text-muted-foreground mb-6">Please login to create questions.</p>
          <Button onClick={() => navigate("/")}>Go to Home</Button>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen">
        <div className="container max-w-xl mx-auto px-4 py-12">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <h1 className="text-3xl font-bold mb-4">Question Submitted!</h1>
            
            {/* Contributor Bonus Display */}
            {contributorBonus > 0 && (
              <div className="mb-6 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400">
                <span className="text-lg font-bold">+{contributorBonus} pts</span>
                <span className="text-sm">Contributor Bonus!</span>
              </div>
            )}
            
            <p className="text-muted-foreground mb-8">
              Your question has been submitted for review. Once approved by an admin, 
              it will appear in the Challenge Pool for other users to solve!
            </p>
            <div className="flex gap-4 justify-center">
              <Button variant="outline" onClick={() => navigate("/")} data-testid="back-home-btn">
                Back to Home
              </Button>
              <Button onClick={() => {
                setSubmitted(false);
                setContributorBonus(0);
                setFormData({
                  title: "", description: "", input_format: "", output_format: "",
                  example_input: "", example_output: "", sample_solution: "# Write your Python solution here\n"
                });
                setTestCases([{ input: "", expected_output: "" }, { input: "", expected_output: "" }]);
                setValidationResult(null);
                setQuickTestInput("");
                setQuickTestOutput("");
                setNav(emptyNav());
                setNavSolution({ code: "", workspace: null });
                setNavVerified(false);
                clearDraft("create-question-nav", user?.user_id);
              }} data-testid="create-another-btn">
                Create Another
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <div className="container max-w-2xl mx-auto px-4 py-8">
        {/* Back Button */}
        <Button
          variant="ghost"
          className="mb-6 gap-2"
          onClick={() => navigate("/")}
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Home
        </Button>

        <h1 className="text-2xl font-bold mb-2">Create a Question</h1>
        <p className="text-muted-foreground mb-8">
          Share your coding challenge with the community. Once approved, it will appear in the Challenge Pool.
        </p>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Track: Junior Blocks | Blocks | Python */}
          <div className="bg-card rounded-xl border border-border p-6 space-y-4" data-testid="create-question-track-card">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div>
                <h2 className="font-semibold text-lg">Which track is this for?</h2>
                <p className="text-sm text-muted-foreground">{isNav ? "Junior Blocks questions are drill navigation grids - draw the grid, then prove it with a block program." : "Blocks questions are solved with drag-and-drop blocks; the sample solution is still written in Python."}</p>
              </div>
              <div className="inline-flex items-center rounded-full border border-border bg-muted/40 p-0.5" role="radiogroup" data-testid="create-question-mode-toggle">
                {MODES.map((v) => ({ v, label: MODE_META[v].label, Icon: MODE_META[v].icon })).map(({ v, label, Icon }) => (
                  <button
                    key={v}
                    type="button"
                    role="radio"
                    aria-checked={track === v}
                    onClick={() => setTrack(v)}
                    className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-sm font-medium transition-colors ${track === v ? "bg-background text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}
                    data-testid={`create-question-mode-${v}`}
                  >
                    <Icon className="w-3.5 h-3.5" /> {label}
                  </button>
                ))}
              </div>
            </div>
            {track === "blocks" && (
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Program inputs <span className="text-xs">(name each value the program receives, in order)</span></label>
                  <div className="mt-2">
                    <BlocksInputsEditor inputs={blocksInputs} onChange={setBlocksInputs} testId="create-question-blocks-inputs" />
                  </div>
                </div>
                <BlockRulesEditor
                  categories={BLOCKS_CATEGORIES}
                  excludedCategories={blocksRules.excluded_categories}
                  excludedBlocks={blocksRules.excluded_blocks}
                  lockedCategories={["program"]}
                  onChange={setBlocksRules}
                  testId="create-question-blocks-rules"
                  hint="Hidden blocks disappear from the toolbox and are rejected on submit."
                />
              </div>
            )}
          </div>

          {isNav && (
            <div className="bg-card rounded-xl border border-border p-6 space-y-4" data-testid="create-question-nav-card">
              <div>
                <h2 className="font-semibold text-lg">Navigation grid *</h2>
                <p className="text-sm text-muted-foreground">Pick a world (Mines, Ocean or Moon), then place the vehicle, goals, obstacles and tunnel pairs. Students program the vehicle with blocks to collect every goal.</p>
              </div>
              <NavGridEditor nav={nav} onChange={updateNav} allowWorlds />
              <div className="border-t border-border pt-4">
                <NavRulesEditor nav={nav} onChange={updateNav} />
              </div>
            </div>
          )}

          {/* Question Details */}
          <div className="bg-card rounded-xl border border-border p-6 space-y-4">
            <h2 className="font-semibold text-lg">Question Details</h2>
            
            <div>
              <label className="text-sm font-medium text-muted-foreground">Title *</label>
              <Input
                name="title"
                value={formData.title}
                onChange={handleChange}
                placeholder="e.g., Sum of Two Numbers"
                className="mt-1"
                required
                data-testid="title-input"
              />
            </div>

            {!isNav && (
            <div>
              <label className="text-sm font-medium text-muted-foreground">Description *</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder="Describe the problem clearly..."
                className="mt-1 w-full min-h-[100px] p-3 rounded-lg bg-background border border-input resize-none"
                required
                data-testid="description-input"
              />
            </div>
            )}

            {!isNav && (<>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-muted-foreground">Input Format</label>
                <textarea
                  name="input_format"
                  value={formData.input_format}
                  onChange={handleChange}
                  placeholder="Describe what input the program expects..."
                  className="mt-1 w-full min-h-[80px] p-3 rounded-lg bg-background border border-input resize-none text-sm"
                  data-testid="input-format-input"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">Output Format</label>
                <textarea
                  name="output_format"
                  value={formData.output_format}
                  onChange={handleChange}
                  placeholder="Describe what output is expected..."
                  className="mt-1 w-full min-h-[80px] p-3 rounded-lg bg-background border border-input resize-none text-sm"
                  data-testid="output-format-input"
                />
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-muted-foreground">Example Input</label>
                <textarea
                  name="example_input"
                  value={formData.example_input}
                  onChange={handleChange}
                  placeholder="5&#10;3"
                  className="mt-1 w-full min-h-[60px] p-3 rounded-lg bg-background border border-input resize-none font-mono text-sm"
                  data-testid="example-input-input"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">Example Output</label>
                <textarea
                  name="example_output"
                  value={formData.example_output}
                  onChange={handleChange}
                  placeholder="8"
                  className="mt-1 w-full min-h-[60px] p-3 rounded-lg bg-background border border-input resize-none font-mono text-sm"
                  data-testid="example-output-input"
                />
              </div>
            </div>
            </>)}
          </div>

          {/* Drill solution (navigation questions) */}
          {isNav && (
            <div className="bg-card rounded-xl border border-border p-6 space-y-4" data-testid="create-question-nav-solution-card">
              <div>
                <h2 className="font-semibold text-lg">Drill program *</h2>
                <p className="text-sm text-muted-foreground">Build a block program that reaches the treasure, then press Play to prove it works. Only a verified program can be submitted.</p>
              </div>
              <BlocksWorkspace
                variant="nav"
                navRules={nav}
                storageId="create-question-nav"
                onChange={onNavChange}
                hidePythonPanel
                height={360}
                title="Your drill program"
                headerExtra={(
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border mr-1 ${navProblem ? "bg-red-500/10 text-red-600 border-red-500/40" : "bg-muted text-muted-foreground border-border"}`} data-testid="create-question-nav-block-count">
                    {navUsed}{nav.max_blocks ? ` / ${nav.max_blocks}` : ""} block{navUsed === 1 ? "" : "s"}
                  </span>
                )}
              />
              <div className="max-w-md">
                <NavPlayback
                  ref={navPlaybackRef}
                  nav={nav}
                  runProgram={runNav}
                  disabled={!navSolution.code || !!navProblem}
                  disabledReason={navProblem || (!navSolution.code ? "Add some blocks under \"when the drill starts\" first" : null)}
                  onRunEnd={(res) => setNavVerified(!!res?.reached)}
                  compact
                />
              </div>
              {navVerified ? (
                <p className="text-sm font-medium text-green-600 flex items-center gap-2" data-testid="create-question-nav-verified"><CheckCircle className="w-4 h-4" /> Your drill program reaches the treasure - ready to submit.</p>
              ) : (
                <p className="text-xs text-muted-foreground" data-testid="create-question-nav-unverified">Press Play and reach the treasure to unlock Submit.</p>
              )}
            </div>
          )}

          {/* Sample Solution */}
          {!isNav && (
          <div className="bg-card rounded-xl border border-border p-6 space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="font-semibold text-lg">Sample Solution *</h2>
                <p className="text-sm text-muted-foreground">
                  Provide a working Python solution. This will be used to verify test cases.
                </p>
              </div>
              <span className="text-xs px-2 py-1 rounded bg-cyan-500/10 text-cyan-600 font-medium">Python</span>
            </div>
            <div className="rounded-lg overflow-hidden border border-zinc-700">
              <CodeMirror
                value={formData.sample_solution}
                height="200px"
                extensions={[python()]}
                theme={oneDark}
                onChange={(value) => {
                  setFormData(prev => ({ ...prev, sample_solution: value }));
                  setValidationResult(null);
                }}
                placeholder="# Write your Python solution here
a = int(input())
b = int(input())
print(a + b)"
                className="text-sm"
                data-testid="solution-input"
              />
            </div>
            
            {/* Quick Test Section */}
            <div className="border-t border-border pt-4 mt-4">
              <div className="flex items-center gap-2 mb-3">
                <Play className="w-4 h-4 text-muted-foreground" />
                <span className="text-sm font-medium">Quick Test</span>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-muted-foreground">Test Input</label>
                  <textarea
                    value={quickTestInput}
                    onChange={(e) => setQuickTestInput(e.target.value)}
                    placeholder="Enter test input..."
                    className="w-full min-h-[60px] p-2 rounded-lg bg-background border border-input resize-none font-mono text-sm mt-1"
                    data-testid="quick-test-input"
                  />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground">Output</label>
                  <div className={`w-full min-h-[60px] p-2 rounded-lg border resize-none font-mono text-sm mt-1 whitespace-pre-wrap ${
                    quickTestOutput.startsWith("Error") 
                      ? "bg-red-500/10 border-red-500/30 text-red-400" 
                      : "bg-zinc-900 border-zinc-700 text-green-400"
                  }`}>
                    {quickTestOutput || <span className="text-muted-foreground">Output will appear here...</span>}
                  </div>
                </div>
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={runQuickTest}
                disabled={quickTesting || !formData.sample_solution.trim()}
                className="mt-3 gap-2"
                data-testid="quick-test-btn"
              >
                {quickTesting ? (
                  <>
                    <Loader2 className="w-3 h-3 animate-spin" />
                    Running...
                  </>
                ) : (
                  <>
                    <Play className="w-3 h-3" />
                    Run Code
                  </>
                )}
              </Button>
            </div>
          </div>
          )}

          {/* Test Cases */}
          {!isNav && (
          <div className="bg-card rounded-xl border border-border p-6 space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="font-semibold text-lg">Test Cases *</h2>
                <p className="text-sm text-muted-foreground">Provide at least 2 test cases to validate solutions.</p>
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addTestCase}
                className="gap-1"
                data-testid="add-test-case-btn"
              >
                <Plus className="w-4 h-4" />
                Add
              </Button>
            </div>

            <div className="space-y-4">
              {testCases.map((tc, index) => (
                <div key={index} className="flex gap-4 items-start">
                  <div className="flex-1 grid sm:grid-cols-2 gap-2">
                    <div>
                      <label className="text-xs text-muted-foreground">Input (supports multiple lines)</label>
                      <textarea
                        value={tc.input}
                        onChange={(e) => handleTestCaseChange(index, "input", e.target.value)}
                        placeholder={"5\n3"}
                        className="w-full min-h-[100px] p-2 rounded-lg bg-background border border-input resize-y font-mono text-sm"
                        data-testid={`test-case-input-${index}`}
                      />
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground">Expected Output (supports multiple lines)</label>
                      <textarea
                        value={tc.expected_output}
                        onChange={(e) => handleTestCaseChange(index, "expected_output", e.target.value)}
                        placeholder={"Line 1\nLine 2\nLine 3"}
                        className="w-full min-h-[100px] p-2 rounded-lg bg-background border border-input resize-y font-mono text-sm"
                        data-testid={`test-case-output-${index}`}
                      />
                    </div>
                  </div>
                  {testCases.length > 2 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="text-destructive hover:bg-destructive/10 mt-5"
                      onClick={() => removeTestCase(index)}
                      data-testid={`remove-test-case-${index}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              ))}
            </div>

            {/* Validation Button */}
            <Button
              type="button"
              variant="outline"
              onClick={validateSolution}
              disabled={validating}
              className="gap-2"
              data-testid="validate-btn"
            >
              {validating ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Validating...
                </>
              ) : (
                <>
                  <Play className="w-4 h-4" />
                  Validate Solution
                </>
              )}
            </Button>

            {/* Validation Results */}
            {validationResult && (
              <div className={`p-4 rounded-lg ${validationResult.allPassed ? "bg-green-500/10 border border-green-500/20" : "bg-red-500/10 border border-red-500/20"}`}>
                <div className="flex items-center gap-2 mb-2">
                  {validationResult.allPassed ? (
                    <>
                      <CheckCircle className="w-5 h-5 text-green-500" />
                      <span className="font-medium text-green-600">All test cases passed!</span>
                    </>
                  ) : (
                    <>
                      <AlertCircle className="w-5 h-5 text-red-500" />
                      <span className="font-medium text-red-600">Some test cases failed</span>
                    </>
                  )}
                </div>
                <div className="space-y-2 text-sm">
                  {validationResult.results.map((r, i) => (
                    <div key={i} className={`p-2 rounded ${r.passed ? "bg-green-500/10" : "bg-red-500/10"}`}>
                      <span className={r.passed ? "text-green-600" : "text-red-600"}>
                        Test {i + 1}: {r.passed ? "PASSED" : "FAILED"}
                      </span>
                      {!r.passed && (
                        <div className="mt-1 text-xs text-muted-foreground">
                          Expected: "{r.expected.trim()}" | Got: "{r.actual?.trim() || r.error}"
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          )}

          {/* Submit Button */}
          <Button
            type="submit"
            className="w-full h-12 text-base"
            disabled={loading || (isNav ? !navVerified : !validationResult?.allPassed)}
            data-testid="submit-question-btn"
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin mr-2" />
                Submitting...
              </>
            ) : (
              "Submit for Review"
            )}
          </Button>

          {!isNav && !validationResult?.allPassed && (
            <p className="text-xs text-center text-muted-foreground">
              You must validate your solution before submitting.
            </p>
          )}
          {isNav && !navVerified && (
            <p className="text-xs text-center text-muted-foreground">
              Play your drill program and reach the treasure before submitting.
            </p>
          )}
        </form>
      </div>
    </div>
  );
}
